<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import zernioSocialInboxes from 'dashboard/api/zernioSocialInboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import SocialConnect from '../components/SocialConnect.vue';

const props = defineProps({
  // 'tiktok' (DMs e comentários), 'googlebusiness' (avaliações do Google) ou
  // 'linkedin' (comentários da página da empresa).
  platform: { type: String, default: 'tiktok' },
});

const { t } = useI18n();
const isGoogle = computed(() => props.platform === 'googlebusiness');
const linkedinTexts = () => ({
  title: t('INBOX_MGMT.ADD.ZERNIO_LINKEDIN.TITLE'),
  desc: t('INBOX_MGMT.ADD.ZERNIO_LINKEDIN.DESC'),
  hint: t('INBOX_MGMT.ADD.ZERNIO_LINKEDIN.HINT'),
});
const texts = computed(() => {
  if (props.platform === 'linkedin') return linkedinTexts();
  return isGoogle.value
    ? {
        title: t('INBOX_MGMT.ADD.ZERNIO_GOOGLE.TITLE'),
        desc: t('INBOX_MGMT.ADD.ZERNIO_GOOGLE.DESC'),
        hint: t('INBOX_MGMT.ADD.ZERNIO_GOOGLE.HINT'),
      }
    : {
        title: t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.TITLE'),
        desc: t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.DESC'),
        hint: t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.BUSINESS_HINT'),
      };
});
const router = useRouter();
const store = useStore();

const accounts = ref([]);
const loading = ref(true);
const creatingId = ref(null);

const loadAccounts = async () => {
  try {
    const { data } = await zernioSocialInboxes.availableAccounts(
      props.platform
    );
    accounts.value = data.accounts || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.ERROR'));
  } finally {
    loading.value = false;
  }
};
onMounted(loadAccounts);

const pendingAccounts = computed(() =>
  accounts.value.filter(account => !account.has_inbox)
);
const linkedAccounts = computed(() =>
  accounts.value.filter(account => account.has_inbox)
);

const handle = account =>
  props.platform !== 'tiktok'
    ? account.username || `#${account.id}`
    : `@${account.username || account.id}`;

// Um botão só (decisão Ivan 08/10): conectou, a caixa já é criada aqui.
const createInbox = async account => {
  creatingId.value = account.id;
  try {
    // Google: o nome padrão vem do servidor ("Avaliações Google <perfil>").
    const { data } = await zernioSocialInboxes.create({
      social_account_id: account.id,
      platform: props.platform,
      name:
        props.platform === 'tiktok'
          ? `TikTok ${account.username || ''}`.trim()
          : undefined,
    });
    await store.dispatch('inboxes/get');
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: data.inbox_id },
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.ERROR')
    );
  } finally {
    creatingId.value = null;
  }
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <h2 class="text-lg font-medium text-n-slate-12">
      {{ texts.title }}
    </h2>
    <p class="mt-1 text-sm text-n-slate-11">
      {{ texts.desc }}
    </p>
    <p class="mt-2 text-xs text-n-amber-11">
      {{ texts.hint }}
    </p>
    <p v-if="loading" class="mt-6 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.LOADING') }}
    </p>
    <template v-else>
      <SocialConnect
        compact
        class="mt-4"
        :platform="platform"
        @connected="createInbox"
      />
      <p v-if="creatingId" class="mt-3 text-xs text-n-slate-10">
        {{ t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.CREATING') }}
      </p>
      <!-- Conta conectada antes (ex.: pelo Estúdio Social) e ainda sem caixa. -->
      <template v-if="accounts.length">
        <p class="mt-8 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.ALREADY_CONNECTED') }}
        </p>
        <ul class="mt-2 space-y-2">
          <li
            v-for="account in [...pendingAccounts, ...linkedAccounts]"
            :key="account.id"
            class="flex items-center justify-between p-3 border rounded-lg border-n-weak"
          >
            <span class="text-sm text-n-slate-12">{{ handle(account) }}</span>
            <span v-if="account.has_inbox" class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.HAS_INBOX') }}
            </span>
            <Button
              v-else
              faded
              :label="t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.USE')"
              :is-loading="creatingId === account.id"
              :disabled="!!creatingId"
              @click="createInbox(account)"
            />
          </li>
        </ul>
      </template>
    </template>
  </div>
</template>
